import Text from '../../../../elements/Typo/Text'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BarChart, Bar, ReferenceArea, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import Button from '../../../../elements/button'
import Icon from '../../../../components/icons'

import { convertToKFormat, getDecimalPlacesByRange } from '../../../../_common/functions'
import DataTableLoader from '../../../../components/DataTableLoader'
import { getChartColor } from '../../../../_config'
import { IoIosWarning } from 'react-icons/io'

const graphColors: any = ['#E5C511', '#D69E77', '#c2c2c2', '#D69E77', '#E5C511']

interface Props {
    data: any[] | null
    kpi: string
    className?: string
    loading: boolean
}
const CustomTooltip = ({ active, payload, decimalDigit }: any) => {
    if (active && payload.length) {
        const { name, ...payloadData } = payload[0].payload
        console.log('CustomTooltip payloadData--', name, payloadData)
        return (
            <div className="bg-white shadow-lg rounded-[0.667rem] p-2">
                <Text className="text-[0.417rem] text-center">{name}</Text>
                <div className="bg-[#EFF2F799] px-2 py-1 rounded-[0.667rem]">
                    {Object.keys(payloadData).map((param: any, i: any) => {
                        if (typeof payloadData[param] === 'object') return null
                        return (
                            <div className="flex items-center gap-2" key={`tooltip-${name}`}>
                                <div
                                    className="w-[0.583rem] h-[0.583rem] rounded-[0.167rem]"
                                    style={{
                                        background: getChartColor(payloadData['group'].is_control ? 'control' : 'test', i),
                                    }}
                                ></div>
                                <Text size="xs" className="flex-grow text-[#74788D] mr-4">
                                    {param}
                                </Text>
                                <Text size="xl" className="font-bold">
                                    {payloadData[param] && Number.isNaN(+payloadData[param]) ? payloadData[param] : convertToKFormat(payloadData[param], decimalDigit)}
                                </Text>
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }
    return null
}
const CustomBarShape = (props: any) => {
    const { x, y, width, height, index: idx, payload } = props
    const fillColor = getChartColor(payload?.group?.is_control ? 'control' : 'test', idx)
    return <rect x={x} y={y} width={width} height={height} fill={fillColor} rx={7} ry={7} />
}

const AvgMetricDesign: React.FC<Props> = ({ data, loading = true, kpi = '', className = '' }) => {
    const { t } = useTranslation()
    const avgMetricTitle = t('Average {{kpi}} Metric', { kpi })
    const resolvedAvgMetricTitle = avgMetricTitle.includes('{{kpi}}') ? `Average ${kpi} Metric` : avgMetricTitle
    const scrollContainerRef = useRef<HTMLDivElement | null>(null)
    const [containerWidth, setContainerWidth] = useState(0)
    const [chartWidth, setChartWidth] = useState(0)
    const [decimalDigit, setDecimalDigit] = useState<number>(2)

    const [zoomArea, setZoomArea] = useState<{
        data: any[]
        refAreaLeft: string
        refAreaRight: string
        top: number
        left: number
        right: number
        bottom: number
    }>({
        data: data || [],
        refAreaLeft: '',
        refAreaRight: '',
        top: 0,
        bottom: 0,
        left: 0,
        right: data ? data.length : 0,
    })

    const getDefaultBarWidth = (rem: number) => {
        const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize)
        return rem * rootFontSize
    }

    // Calculate minimum width needed for non-overlapping bars
    const barGapWidth = 8 // Gap between bars in the same group
    const barWidth = getDefaultBarWidth(1.25) // Width in pixels for each individual bar

    const calculateRequiredWidth = () => {
        const barCount = zoomArea.data ? zoomArea.data.length : 0

        // Calculate the total width needed for all bars without overlapping
        const requiredWidth = barCount * barWidth
        setChartWidth(requiredWidth)
    }

    useEffect(() => {
        if (scrollContainerRef.current) {
            // Get the width of the parent container
            setContainerWidth(scrollContainerRef.current.clientWidth)
        }

        // Add resize listener
        const handleResize = () => {
            if (scrollContainerRef.current) {
                setContainerWidth(scrollContainerRef.current.clientWidth)
            }
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const selectZoneAndZoomIn = () => {
        if (data) {
            const { left, right } = zoomArea
            const middle = Math.floor((left + right) / 2)
            const currentRange = right - left
            const zoomPercentage = 0.2
            const shiftingBy = Math.max(1, Math.floor(currentRange * zoomPercentage))
            const updatedLeft = Math.min(middle, left + shiftingBy)
            const updatedRight = Math.max(middle, right - shiftingBy)
            const selectedData = data.slice(updatedLeft, updatedRight)

            if (selectedData.length) {
                setZoomArea({
                    ...zoomArea,
                    data: selectedData,
                    refAreaLeft: '',
                    refAreaRight: '',
                    left: updatedLeft,
                    right: updatedRight,
                })
            }
        }
    }

    const handleMouseUp = () => {
        if (data) {
            let { refAreaLeft, refAreaRight } = zoomArea
            if (/*refAreaLeft === refAreaRight ||*/ refAreaRight === '') {
                setZoomArea((prevState: any) => ({
                    ...prevState,
                    refAreaLeft: '',
                    refAreaRight: '',
                }))
                return
            }
            const indx = data?.findIndex((i) => i.name === refAreaLeft)
            const indx2 = data?.findIndex((i) => i.name === refAreaRight)

            if (indx > indx2) [refAreaLeft, refAreaRight] = [refAreaRight, refAreaLeft]
            setZoomArea((prevState: any) => ({
                ...prevState,
                data: prevState.data.slice(indx, indx2 + 1),
                refAreaLeft: '',
                refAreaRight: '',
                left: indx,
                right: indx2,
            }))
        }
    }

    const zoomOut = () => {
        setZoomArea({
            data: data || [],
            refAreaLeft: '',
            refAreaRight: '',
            top: 0,
            bottom: 0,
            left: 0,
            right: data?.length || 0,
        })
    }

    const handleMouseDown = (e: any) => {
        if (e?.activeLabel) {
            setZoomArea((prevState: any) => ({
                ...prevState,
                refAreaLeft: e.activeLabel,
            }))
        }
    }

    const graphPageCount: number = 13
    const [graphPagination, setGraphPagination] = useState<number>(0)

    useEffect(() => {
        calculateRequiredWidth()
    }, [containerWidth, zoomArea.data])

    useEffect(() => {
        if (data?.length) {
            setZoomArea({
                data,
                refAreaLeft: '',
                refAreaRight: '',
                top: 0,
                bottom: 0,
                left: 0,
                right: data.length,
            })
            const keys: string[] = Object.keys(data[0])
            const values: any[] = []
            for (const gr of keys.filter((k) => k !== 'name')) {
                values.push(data[0][gr])
            }
            setDecimalDigit(getDecimalPlacesByRange(values))
        }
    }, [data])

    console.log('AvgMetricDesign--data--', data)

    const render = () => {
        if (loading) {
            return (
                <div className="mt-5">
                    <DataTableLoader className="h-4 w-4" />
                </div>
            )
        } else if (data) {
            return (
                <div className={`border border-[#0000001A] rounded-[0.625rem] p-2 overflow-x-scroll ${className}`} ref={scrollContainerRef}>
                    <div className="flex items-center justify-between mb-2">
                        <Text size="lg" className="font-semibold">
                            {resolvedAvgMetricTitle}
                        </Text>
                        <div className="flex items-center justify-between mt-1 shadow-1 rounded-md w-24">
                            <button type="button" className="text-center w-1/2 cursor-pointer hover:bg-[#F5F5F5] rounded-e" onClick={zoomOut} title={t('Reset Zoom')}>
                                <span className="font-normal text-nowrap">-</span>
                            </button>
                            <button type="button" className="border-[#D9D9D9] border-r text-center w-1/2 cursor-pointer hover:bg-[#F5F5F5] rounded-s" title={t('Zoom In')} onClick={selectZoneAndZoomIn}>
                                <span className="font-normal text-nowrap">+</span>
                            </button>
                        </div>
                    </div>
                    <div className="h-[18.542rem] text-[0.5rem] flex relative" style={{ width: `${chartWidth}px`, minWidth: '100%' }}>
                        <div className="w-[1rem] flex items-center justify-center">
                            <p className="text-[0.5rem] text-center leading-none font-semibold -rotate-90 whitespace-nowrap">Avg. {kpi}</p>
                        </div>

                        <ResponsiveContainer width="100%" height="100%" className="flex-grow">
                            <BarChart
                                data-testid="custom-bar"
                                width={chartWidth}
                                barGap={barGapWidth}
                                height={374}
                                data={zoomArea.data.slice(graphPagination * graphPageCount, graphPagination * graphPageCount + graphPageCount)}
                                margin={{
                                    top: 20,
                                    right: 0,
                                    left: 0,
                                    bottom: data.length > 12 ? 60 : 0,
                                }}
                                onMouseDown={handleMouseDown}
                                onMouseUp={handleMouseUp}
                                onMouseMove={(e) => {
                                    if (zoomArea.refAreaLeft) {
                                        setZoomArea((prevState: any) => ({
                                            ...prevState,
                                            refAreaRight: e?.activeLabel || '',
                                        }))
                                    }
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" stroke="#939393" tickLine={false} />
                                <YAxis overlineThickness={1} tickLine={false} axisLine={false} width={25} />
                                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                                <Bar
                                    dataKey={'value'}
                                    radius={[7, 7, 7, 7]}
                                    barSize={getDefaultBarWidth(1.25)}
                                    label={{
                                        position: 'top',
                                        textAnchor: 'middle',
                                        fill: 'black',
                                        formatter: (value: any) => (value && Number.isNaN(+value) ? value : convertToKFormat(+value, decimalDigit)),
                                    }}
                                    shape={CustomBarShape}
                                />
                                {zoomArea.refAreaLeft && zoomArea.refAreaRight ? <ReferenceArea x1={zoomArea.refAreaLeft} x2={zoomArea.refAreaRight} strokeOpacity={0.3} /> : null}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className={`flex items-center ${zoomArea?.data?.length > 13 ? 'justify-between' : 'justify-center'}`}>
                        {zoomArea?.data?.length > 13 && (
                            <Button
                                theme="tertiary"
                                size="xxs"
                                disabled={graphPagination == 0}
                                onClick={() => {
                                    setGraphPagination((prev) => {
                                        if (prev > 0) {
                                            return prev - 1
                                        }
                                        return prev
                                    })
                                }}
                            >
                                <Icon name="arrow-left" className="text-[0.75rem] text-[#000000A6]" />
                            </Button>
                        )}
                        <p className="text-[0.5rem] text-center leading-none font-semibold  whitespace-nowrap">{t('Group')}</p>
                        {zoomArea?.data?.length > 13 && (
                            <Button
                                theme="tertiary"
                                size="xxs"
                                disabled={zoomArea?.data?.length <= graphPagination * graphPageCount + graphPageCount}
                                onClick={() => {
                                    setGraphPagination((prev) => {
                                        if (prev < Math.ceil(data.length / graphPageCount) - 1) {
                                            return prev + 1
                                        }
                                        return prev
                                    })
                                }}
                            >
                                <Icon name="arrow-right" className="text-[0.75rem] text-[#000000A6]" />
                            </Button>
                        )}
                    </div>
                </div>
            )
        }
        return (
            <p className="italic flex items-center p-2 leading-[1.2] rounded-md font-medium text-red-500 text-[0.6rem] w-full border border-orange-300 bg-orange-100 ">
                <IoIosWarning /> &nbsp; Average Metric Design Data Not Found.
            </p>
        )
    }

    return <>{render()}</>
}

export default AvgMetricDesign
